import { useEffect, useRef, useState } from 'react';

const clients = [
  {
    icon: 'ri-scales-3-line',
    color: '#00d4ff',
    title: 'Law Firms',
    tagline: 'Dominate locally',
    desc: 'Get your firm name in front of thousands daily. When accidents happen, they\'ll remember YOU.',
  },
  {
    icon: 'ri-home-4-line',
    color: '#39ff14',
    title: 'Realtors',
    tagline: 'Sell faster',
    desc: 'Drive your listings and brand through neighborhoods where buyers live and work.',
  },
  {
    icon: 'ri-car-line',
    color: '#b026ff',
    title: 'Car Dealerships',
    tagline: 'Drive more traffic',
    desc: 'Announce deals and new arrivals to thousands of Tampa drivers — your ideal customers.',
  },
  {
    icon: 'ri-restaurant-2-line',
    color: '#ff073a',
    title: 'Restaurants',
    tagline: 'Fill every table',
    desc: 'Put your food and brand directly in front of hungry people during lunch and dinner rush hours.',
  },
  {
    icon: 'ri-calendar-event-line',
    color: '#ff9500',
    title: 'Events & Promos',
    tagline: 'Maximum buzz',
    desc: 'Concerts, grand openings, sales events — we amplify your event to thousands before it starts.',
  },
];

const marketingPhrases = [
  { label: 'A/B Variant 1', text: '"The most known beats the best"' },
  { label: 'A/B Variant 2', text: '"We take your brand to the streets"' },
  { label: 'A/B Variant 3', text: '"Best price per impression"' },
  { label: 'A/B Variant 4', text: '"Mobile advertising that moves your business"' },
  { label: 'A/B Variant 5', text: '"Visibility that converts"' },
  { label: 'A/B Variant 6', text: '"Your brand everywhere people go"' },
];

const TargetClients = () => {
  const [visible, setVisible] = useState(false);
  const [activePhrase, setActivePhrase] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhrase((prev) => (prev + 1) % marketingPhrases.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="clients" className="relative py-24 md:py-32 bg-dark-base overflow-hidden" ref={sectionRef}>
      {/* Animated BG particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-10 animate-float"
            style={{
              width: 200 + i * 50,
              height: 200 + i * 50,
              left: `${(i * 13) % 100}%`,
              top: `${(i * 17) % 100}%`,
              background: `radial-gradient(circle, ${['#00d4ff','#39ff14','#b026ff','#ff073a'][i % 4]}, transparent)`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${5 + i}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div
            className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: '#ff073a', border: '1px solid #ff073a', boxShadow: '0 0 10px rgba(255,7,58,0.3)' }}
          >
            Who We Serve
          </div>
          <h2
            className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl"
            style={{ color: '#ffffff', textShadow: '0 0 30px rgba(255,7,58,0.3)' }}
          >
            WE DELIVER{' '}
            <span style={{ color: '#ff073a', textShadow: '0 0 20px #ff073a' }}>ATTENTION</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg font-rajdhani italic">
            &quot;If your business needs attention, we deliver it&quot;
          </p>
        </div>

        {/* Client Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {clients.map((c, i) => (
            <div
              key={c.title}
              className={`group relative p-6 rounded-2xl bg-dark-card text-center cursor-pointer transition-all duration-500 hover:scale-105 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                border: `1px solid ${c.color}25`,
                transitionDelay: `${i * 100}ms`,
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = c.color;
                el.style.boxShadow = `0 0 30px ${c.color}30`;
                el.style.backgroundColor = `${c.color}08`;
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = `${c.color}25`;
                el.style.boxShadow = 'none';
                el.style.backgroundColor = '';
              }}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ backgroundColor: `${c.color}15` }}>
                <i
                  className={`${c.icon} text-3xl group-hover:scale-110 transition-transform duration-300`}
                  style={{ color: c.color, textShadow: `0 0 15px ${c.color}` }}
                />
              </div>
              <h3 className="font-rajdhani font-bold text-lg text-white mb-1">{c.title}</h3>
              <div className="text-xs font-rajdhani tracking-wider uppercase mb-3" style={{ color: c.color }}>
                {c.tagline}
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom Statement */}
        <div
          className={`text-center mt-16 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p
            className="font-orbitron font-black text-xl md:text-2xl lg:text-3xl"
            style={{ color: '#ffffff', textShadow: '0 0 20px rgba(57,255,20,0.3)' }}
          >
            Perfect for brands that want to{' '}
            <span style={{ color: '#39ff14', textShadow: '0 0 15px #39ff14' }}>dominate locally</span>
          </p>
        </div>

        {/* Marketing A/B Phrases */}
        <div
          className={`mt-14 p-8 rounded-3xl bg-dark-card transition-all duration-700 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ border: '1px solid rgba(176,38,255,0.2)' }}
        >
          <div className="text-center mb-6">
            <div
              className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3"
              style={{ color: '#b026ff', border: '1px solid #b026ff30', backgroundColor: '#b026ff10' }}
            >
              Marketing Messages — A/B Testing Variants
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {marketingPhrases.map((phrase, i) => (
              <div
                key={i}
                className="px-4 py-2 rounded-full text-sm font-rajdhani font-semibold cursor-pointer transition-all duration-300"
                style={{
                  backgroundColor: activePhrase === i ? 'rgba(176,38,255,0.2)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${activePhrase === i ? '#b026ff' : 'rgba(255,255,255,0.1)'}`,
                  color: activePhrase === i ? '#b026ff' : '#9ca3af',
                  boxShadow: activePhrase === i ? '0 0 15px rgba(176,38,255,0.3)' : 'none',
                }}
                onClick={() => setActivePhrase(i)}
              >
                {phrase.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TargetClients;
